import Cookies from 'js-cookie';

import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams, useRoutes, useSearchParams } from 'react-router-dom';

import { ErrorFallback, Loading, NotFound, UnauthorizedFallback } from '@/components';
import { UnderConstructionFallback } from '@/components/UnderConstructionFallback';
import { ExtranetHandler } from '@/fakeExtranet/ExtranetHandler';
import { CreateNewRequest } from '@/features/borderCrossingRORO/createNewRequest';
import { SubscribeAdditionnalInformationForm } from '@/features/borderCrossingRORO/onBoarding';
import { CookiesList, PrivacyStatement } from '@/features/customsPass/informations';
import { NavigationSwitcher } from '@/features/easy/NavigationSwitcher';
import { PreFolderQualificationSwitcher } from '@/features/easy/preFolderQualification';
import { WithFeatureFlagForPage } from '@/features/featuresFlag/WithFeatureFlagForPage';
import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { useFeaturesFlagState } from '@/features/featuresFlag/hooks';
import { FoldersTable } from '@/features/folders/FoldersTable';
import { MainLayout } from '@/features/layouts';
import { MainLayoutWithOnlyLogout } from '@/features/layouts/MainLayoutWithOnlyLogout';
import { SecurityPolicy } from '@/features/securityPolicy/SecurityPolicy';
import { LandingPage } from '@/features/sherpass/landingPage';
import { ServicesOffer } from '@/features/sherpass/servicesOffer/ServicesOffer';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { stylesCenteredLoader } from '@/utils/globalStyles';
import lazyImport from '@/utils/lazyImport';

function App() {
  return (
    <MainLayout>
      <Suspense fallback={<Loading sx={stylesCenteredLoader} />}>
        <Outlet />
      </Suspense>
    </MainLayout>
  );
}

function AuthPage() {
  const { folderId } = useParams();
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();

  const authToken = searchParams.get('authToken');
  useEffect(() => {
    if (!authToken) {
      window.location.href = process.env.REACT_APP_PORTAL_URL;
    } else {
      Cookies.set('authToken', authToken);
    }
  }, [authToken, searchParams]);

  if (authToken && folderId) {
    return <Navigate to={`/folders/${folderId}`} />;
  }

  if (authToken) {
    return <Navigate to='/folders' />;
  }

  return <p>{t('common.unauthorized')}</p>;
}
const ExtranetHandlerWithFeatureFlag = WithFeatureFlagForPage(ExtranetHandler);

const { EasyFoldersRoutes } = lazyImport(() => import('@/features/easy/routes'), 'EasyFoldersRoutes');
const { CustomsPassRoutes } = lazyImport(() => import('@/features/customsPass/routes'), 'CustomsPassRoutes');
const { BorderCrossingRoroRoutes } = lazyImport(
  () => import('@/features/borderCrossingRORO/routes'),
  'BorderCrossingRoroRoutes',
);

export function AppRoutes() {
  const { featuresFlag } = useFeaturesFlagState();

  const bpeRoutes = [
    {
      path: '/redirect',
      skipLazyLoad: true,
      element: <AuthPage />,
      children: [
        {
          path: '/redirect/folders',
          skipLazyLoad: true,
          element: <AuthPage />,
        },
        {
          path: '/redirect/folders/:folderId',
          skipLazyLoad: true,
          element: <AuthPage />,
        },
      ],
    },
    {
      path: '/extranet',
      element: (
        <ProtectedRoute>
          <ExtranetHandlerWithFeatureFlag featureFlagKey='extranet_mock' />
        </ProtectedRoute>
      ),
    },
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <App />
        </ProtectedRoute>
      ),
      children: [
        {
          path: '',
          skipLazyLoad: true,
          element: <Navigate to='/folders' />,
        },
        {
          path: '/folders',
          skipLazyLoad: true,
          element: <FoldersTable />,
        },
        {
          path: '/pre-folders/:folderId/qualification',
          element: <PreFolderQualificationSwitcher />,
        },
        {
          path: '/folders/:folderId',
          element: <NavigationSwitcher />,
        },
        {
          path: '/folders/*',
          element: <EasyFoldersRoutes />,
        },
        {
          path: '*',
          element: <Navigate to='/404' />,
        },
      ],
    },
    {
      path: '/information',
      element: (
        <ProtectedRoute>
          <App />
        </ProtectedRoute>
      ),
      children: [
        {
          path: '/information/security-policy',
          skipLazyLoad: true,
          element: <SecurityPolicy />,
        },
      ],
    },
    {
      path: '/401',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnauthorizedFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
    {
      path: '/404',
      element: <NotFound />,
    },
    {
      path: '/500',
      element: <ErrorFallback />,
    },
    {
      path: '/502',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnderConstructionFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
    {
      path: '/503',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnderConstructionFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
    {
      path: '/504',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnderConstructionFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
  ];

  const sherpassRoutes = [
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <App />
        </ProtectedRoute>
      ),
      children: [
        {
          path: '',
          skipLazyLoad: true,
          element: <Navigate to='/landing-page' />,
        },
        {
          path: '/folders',
          skipLazyLoad: true,
          element: <FoldersTable />,
        },
        {
          path: '/landing-page',
          skipLazyLoad: true,
          element: <LandingPage />,
        },
        {
          path: '/service-offers',
          skipLazyLoad: true,
          element: <ServicesOffer />,
        },
        {
          path: '/folders/*',
          element: <CustomsPassRoutes />,
        },
        {
          path: '/typologies/:folderTypologyId/folders/*',
          element: <BorderCrossingRoroRoutes />,
        },
        {
          path: '/new-request',
          element: <CreateNewRequest />,
        },
        {
          path: '/subscribe/additional-information',
          element: <SubscribeAdditionnalInformationForm />,
        },
        {
          path: '*',
          element: <Navigate to='/404' />,
        },
      ],
    },
    {
      path: '/information',
      element: (
        <ProtectedRoute>
          <App />
        </ProtectedRoute>
      ),
      children: [
        {
          path: '/information/security-policy',
          skipLazyLoad: true,
          element: <SecurityPolicy />,
        },
        {
          path: '/information/privacy-statement',
          skipLazyLoad: true,
          element: <PrivacyStatement />,
        },
        {
          path: '/information/cookies-list',
          skipLazyLoad: true,
          element: <CookiesList />,
        },
        {
          path: '*',
          element: <Navigate to='/404' />,
        },
      ],
    },
    {
      path: '/401',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnauthorizedFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
    {
      path: '/404',
      element: <NotFound />,
    },
    {
      path: '/500',
      element: <ErrorFallback />,
    },
    {
      path: '/502',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnderConstructionFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
    {
      path: '/503',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnderConstructionFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
    {
      path: '/504',
      element: (
        <MainLayoutWithOnlyLogout>
          <UnderConstructionFallback />
        </MainLayoutWithOnlyLogout>
      ),
    },
  ];

  const element = useRoutes(featuresFlag.get(FEATURES_FLAG_KEYS.BPE) ? bpeRoutes : sherpassRoutes);

  return element;
}
